import axios from 'axios';
import { extractControllerFromUrl, extractFunnelPageIdFromUrl } from '../Util/URLBuilder';
/**
 * HookRedeems-specific form submission implementation
 * This replaces the checkForm function from page.php
 */
export const submitForm = async (submitButton, form, options = {}) => {
    // Extract options
    const { turnstileToken, ajaxUrl } = options;

    // Default translation function
    const defaultT = key => key;
    const t = typeof options.t === 'function' ? options.t : defaultT;
    const openAlertFn = options.openAlert;

    // Unified alert helper
    const showAlert = message => {
        try {
            if (typeof openAlertFn === 'function') {
                openAlertFn(message);
            } else if (typeof window.openAlert === 'function') {
                window.openAlert(message, t);
            } else {
                alert(message);
            }
        } catch (e) {
            console.error('Error showing alert:', e);
            alert(message);
        }
    };

    try {
        // Show loading indicator
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'on');
            submitButton.disabled = true;
        }

        // Helper function to check if a field is mandatory
        const isFieldMandatory = (field) => {
            if (!field) return false;
            // Check HTML5 required attribute
            if (field.hasAttribute('required') && field.getAttribute('required') !== 'false') {
                return true;
            }
            // Check if parent has 'required' class
            let parent = field.closest('.form-group, .fv-row');
            if (parent && parent.classList.contains('required')) {
                return true;
            }
            // Check FormValidation.io if available
            if (window.fv || window.globalFormValidator) {
                const validator = window.globalFormValidator || window.fv;
                if (validator && validator.elements && field.name) {
                    const fieldConfig = validator.elements[field.name];
                    if (fieldConfig && fieldConfig.validators && fieldConfig.validators.notEmpty) {
                        return true;
                    }
                }
            }
            return false;
        };
        
        // Check if one_offer_display is false (indicated by hidden input)
        const hiddenRedeemItemSelect = document.getElementById('hidden-redeem-item-select');
        const oneOfferDisplay = !hiddenRedeemItemSelect;
        
        // Redeem-specific validation: ensure at least one item is selected
        // Skip this check if one_offer_display is false
        if (oneOfferDisplay) {
            const selected = form.querySelectorAll('.hook-redeem-item-id:checked');
            const allItems = form.querySelectorAll('.hook-redeem-item-id');
            if (allItems.length > 0 && selected.length === 0) {
                // Check if any redeem item field is mandatory
                const isRedeemItemMandatory = Array.from(allItems).some(field => isFieldMandatory(field));
                if (isRedeemItemMandatory) {
                    if (submitButton) {
                        submitButton.setAttribute('data-kt-indicator', 'off');
                        submitButton.disabled = false;
                    }
                    const msg = '請選擇禮品';
                    showAlert(msg);
                    const err = new Error(msg);
                    err.code = 'REDEEM_REQUIRED';
                    throw err;
                }
            }
        }

        // Prepare AJAX URL
        const requestUrl = ajaxUrl || null;
        if (!requestUrl) {
            throw new Error(t('提交表單時發生錯誤，請重試。'));
        }

        // Collect form data directly from form fields
        const formData = new FormData(form);

        // Ensure _csrfToken is included
        const csrfInput = document.querySelector('input[name="_csrfToken"]');
        let csrfToken = null;
        if (csrfInput && csrfInput.value && !formData.has('_csrfToken')) {
            formData.append('_csrfToken', csrfInput.value);
            csrfToken = csrfInput.value;
            console.log('HookRedeems: Added _csrfToken:', csrfToken);
        } else if (csrfInput && csrfInput.value) {
            csrfToken = csrfInput.value;
        }

        const dataStr = new URLSearchParams(formData).toString();

        // Include Turnstile token if available
        if (turnstileToken) {
            axios.defaults.headers.common['X-Turnstile-Token'] = turnstileToken;
        }

        // Prepare headers
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
        };
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }

        // Submit via AJAX
        const response = await axios.post(requestUrl, dataStr, {
            headers: headers
        });

        const data = response.data;
        if (data.require_turnstile) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = data.rm || 'Security verification required. Please try again.';
            showAlert(errorMessage);
            return { success: false, require_turnstile: true, message: errorMessage };
        }
        if (data.rc === 1 || data.rc === true) {
            // On success, submit the form normally to follow Cake redirects
            if (form.getAttribute('action')) {
				const inIframe = (() => {
					try {
						return window.self !== window.top;
					} catch (e) {
						return true;
					}
				})();

				if (inIframe) {
					const actionUrl = form.getAttribute('action');
					headers['X-Iframe'] = '1';
					const resp = await axios.post(actionUrl, dataStr, { headers: headers });
					const finalUrl = resp?.request?.responseURL;
					window.location.href = finalUrl || actionUrl;
					return { success: true, submitted: true, redirected: true };
				}

                form.submit();
                return { success: true };
            }
            return { success: true };
        } else {
            // Server validation failed
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const msg = data.rm || t('表單驗證失敗');
            showAlert(msg);
            return { success: false, message: msg };
        }
    } catch (error) {
        console.error('HookRedeems: Form submission error', error);
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'off');
            submitButton.disabled = false;
        }
        const message = error.message || t('系統發生錯誤，請稍後再試。');
        showAlert(message);
        throw error;
    }
};

/**
 * Hook to provide form submission functionality
 */
export const useFormSubmission = () => {
    const getFormData = () => {
        const form = document.getElementById('reg-form');
        if (!form) {
            console.error('Form not found');
            return '';
        }
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    };
    return { submitForm, getFormData };
};

/**
 * Creates and attaches the global checkForm function
 */
export const createGlobalCheckForm = submitFormImpl => {
    const original = window.checkForm;
    window.checkForm = async (formDataStr, setButtonIndicator, openAlertFn, ajaxUrl, enablePayment, submitBtn) => {
        try {
            const form = document.getElementById('reg-form');
            const options = {
                enablePayment,
                openAlert: openAlertFn,
                ajaxUrl,
                controller: extractControllerFromUrl(ajaxUrl),
                funnel_page_id: extractFunnelPageIdFromUrl(ajaxUrl)
            };
            return await submitFormImpl(submitBtn, form, options);
        } catch (err) {
            console.error('Error in redeem checkForm wrapper:', err);
            if (setButtonIndicator) setButtonIndicator('off');
            if (openAlertFn) openAlertFn('系統發生錯誤，請稍後再試。');
            return { success: false, error: err };
        }
    };
    return original;
};

export default { submitForm, useFormSubmission, createGlobalCheckForm }; 